/* eslint-disable max-lines */
import { useEffect, useMemo } from 'react';
import {
  normalizeToE164Like,
  isPhoneValidForForm
} from '@components/atoms/ui-kit/phone-input.tsx';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUpdateMe } from '@utils/api/users/use-update-me.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import { useUpdateOrganization } from '@utils/api/organizations/use-update-organization.tsx';
import { useTranslation } from 'react-i18next';
import AccountDetailsForm from '@components/profile/account-details-form.tsx';
import OrganizationDetailsForm from '@components/profile/organization-details-form.tsx';
import SecuritySettings from '@components/profile/security-settings.tsx';
import DeleteAccountSettings from '@components/profile/delete-account-settings.tsx';
import { useGetMe } from '@utils/api/users/use-get-me.tsx';
import { Organization } from '@utils/types/organization/organization.ts';
import AccountPersonalization from '@components/profile/account-personalization.tsx';
import Keymap from '@components/profile/keymap-functions.tsx';
import { UserResponse } from '@utils/types/profile/user-profile.ts';
import toast from 'react-hot-toast';
import Tabs, { Tab } from '@components/atoms/ui-kit/tabs.tsx';
import {
  User,
  Building2,
  FileText,
  Keyboard,
  Shield,
  Plug
} from 'lucide-react';
import Connections from '@components/profile/connections/connections.tsx';
import { useIsPublishingEnabled } from '@utils/api/connections/use-is-publishing-enabled.tsx';

export default function Profile() {
  const { t } = useTranslation();

  const { data: organization } = useOrganization();
  const { data: user } = useGetMe();
  const { isEnabled: publishingEnabled } = useIsPublishingEnabled();

  const { mutate: updateMe } = useUpdateMe();
  const updateOrganizationMutation = useUpdateOrganization();

  // Check if user has admin or sysop role
  const userRoles = (user?.result?.roles ?? []) as unknown as string[];
  const isAdmin = userRoles.includes('admin') || userRoles.includes('sysop');

  const accountDetailsSchema = yup.object().shape({
    first_name: yup.string().required(t('first-name-is-required')),
    last_name: yup.string().required(t('last-name-is-required')),
    email: yup
      .string()
      .email(t('invalid-email'))
      .required(t('email-is-required')),
    newsletter: yup.boolean()
  });

  const organizationSchema = yup.object().shape({
    name: yup.string().required(t('organization-name-is-required')),
    email: yup
      .string()
      .email(t('invalid-email'))
      .required(t('email-is-required')),
    phone: yup
      .string()
      .optional()
      .test(
        'is-valid-phone',
        t('enter-a-valid-international-phone-number'),
        isPhoneValidForForm
      ),
    address_street: yup.string().required(t('address-street-is-required')),
    address_city: yup.string().optional(),
    address_postal_code: yup.string().optional(),
    address_country_code: yup.string().optional(),
    billing_vat: yup.string().required(t('billing-vat-is-required')),
    diarization: yup.boolean().optional(),
    allow_magic_link_export: yup.boolean().optional(),
    reseller_branding: yup
      .object()
      .shape({
        brand_logo: yup.string().optional(),
        brand_color: yup.string().optional(),
        brand_name: yup.string().optional()
      })
      .optional()
  });

  const {
    control: accountControl,
    handleSubmit: handleAccountSubmit,
    reset: resetAccount,
    formState: { errors: accountErrors, isDirty: isAccountDirty }
  } = useForm({
    defaultValues: {
      first_name: user?.result.first_name || '',
      last_name: user?.result.last_name || '',
      email: user?.result.email || '',
      newsletter: user?.result.newsletter || false
    },
    resolver: yupResolver(accountDetailsSchema)
  });

  const {
    control: organizationControl,
    handleSubmit: handleOrganizationSubmit,
    setValue,
    reset: resetOrganization,
    formState: { errors: organizationErrors, isDirty: isOrganizationDirty }
  } = useForm<{
    name: string;
    email: string;
    phone?: string;
    address_street: string;
    address_city?: string;
    address_postal_code?: string;
    address_country_code?: string;
    billing_vat: string;
    diarization?: boolean;
    allow_magic_link_export?: boolean;
    default_translation_languages?: string[];
    reseller_branding?: {
      brand_logo?: string;
      brand_color?: string;
      brand_name?: string;
    };
  }>({
    defaultValues: {
      name: organization?.name || '',
      email: organization?.email || '',
      phone: organization?.phone || '',
      address_street: organization?.address_street || '',
      address_city: organization?.address_city || '',
      address_postal_code: organization?.address_postal_code || '',
      address_country_code: organization?.address_country_code || '',
      billing_vat: organization?.billing_vat || '',
      diarization: organization?.diarization || false,
      reseller_branding: {
        brand_logo: organization?.reseller_branding?.brand_logo || '',
        brand_color: organization?.reseller_branding?.brand_color || '',
        brand_name: organization?.reseller_branding?.brand_name || ''
      },
      allow_magic_link_export: organization?.allow_magic_link_export ?? true,
      default_translation_languages:
        organization?.default_translation_languages || []
    },
    resolver: yupResolver(organizationSchema)
  });

  const onSubmitAccount = (data: UserResponse) => {
    const userData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      newsletter: data.newsletter
    };

    updateMe(userData, {
      onSuccess: () => {
        toast.success(t('account-details-updated-successfully'));
      }
    });
  };

  const onSubmitOrganization = (data: Organization) => {
    const organizationDataUpdate: Organization = {
      ...data,
      address_country_code: data.address_country_code?.toLowerCase(),
      diarization: data.diarization
    };

    // Treat dial-code-only phone (e.g. "+31") as empty — PhoneField
    // auto-sets the dial code even when the user never entered a local number.
    if (
      organizationDataUpdate.phone &&
      /^\+?\d{0,4}$/.test(organizationDataUpdate.phone)
    ) {
      organizationDataUpdate.phone = '';
    }

    // Strip empty optional strings so backend doesn't reject them
    // (backend treats these as "optional OR min-length", not "optional OR empty").
    const optionalStringKeys: (keyof Organization)[] = [
      'phone',
      'address_city',
      'address_postal_code',
      'address_country_code'
    ];
    optionalStringKeys.forEach(key => {
      if (organizationDataUpdate[key] === '') {
        delete organizationDataUpdate[key];
      }
    });

    if (organizationDataUpdate.reseller_branding?.brand_color === '') {
      delete organizationDataUpdate.reseller_branding.brand_color;
    }

    updateOrganizationMutation.mutate(organizationDataUpdate, {
      onSuccess: () => {
        toast.success(t('organization-details-updated-successfully'));
      }
    });
  };

  useEffect(() => {
    if (user) {
      resetAccount({
        first_name: user.result.first_name || '',
        last_name: user.result.last_name || '',
        email: user.result.email || '',
        newsletter: user.result.newsletter || false
      });
    }
  }, [user, resetAccount]);

  useEffect(() => {
    if (organization) {
      resetOrganization({
        name: organization.name || '',
        email: organization.email || '',
        phone: normalizeToE164Like(
          organization.phone || '',
          organization.address_country_code || 'NL'
        ),
        address_street: organization.address_street || '',
        address_city: organization.address_city || '',
        address_postal_code: organization.address_postal_code || '',
        address_country_code: organization.address_country_code || '',
        billing_vat: organization.billing_vat || '',
        diarization: organization.diarization || false,
        reseller_branding: {
          brand_logo: organization.reseller_branding?.brand_logo || '',
          brand_color: organization.reseller_branding?.brand_color || '',
          brand_name: organization.reseller_branding?.brand_name || ''
        },
        allow_magic_link_export: organization.allow_magic_link_export ?? true,
        default_translation_languages:
          organization.default_translation_languages || []
      });
    }
  }, [organization, resetOrganization]);

  // Build tabs dynamically based on user role
  const tabs: Tab[] = useMemo(() => {
    const baseTabs: Tab[] = [
      {
        id: 0,
        name: (
          <span className="flex items-center gap-2">
            <User className="h-4 w-4" />
            {t('account')}
          </span>
        ),
        component: (
          <div className="py-4">
            <AccountDetailsForm
              control={accountControl}
              errors={accountErrors}
              onSubmit={onSubmitAccount}
              user={user}
              handleAccountSubmit={handleAccountSubmit}
              isAccountDirty={isAccountDirty}
            />
          </div>
        )
      }
    ];

    // Add admin-only tabs
    if (isAdmin) {
      baseTabs.push({
        id: 1,
        name: (
          <span className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t('organization')}
          </span>
        ),
        component: (
          <div className="py-4">
            <OrganizationDetailsForm
              control={organizationControl}
              errors={organizationErrors}
              onSubmit={onSubmitOrganization}
              organizationData={organization}
              handleOrganzationSubmit={handleOrganizationSubmit}
              isOrganizationDirty={isOrganizationDirty}
              setValue={setValue}
            />
          </div>
        )
      });

      baseTabs.push({
        id: 2,
        name: (
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            {t('templates')}
          </span>
        ),
        component: (
          <div className="py-4">
            <AccountPersonalization />
          </div>
        )
      });

      if (publishingEnabled) {
        baseTabs.push({
          id: 5,
          name: (
            <span className="flex items-center gap-2">
              <Plug className="h-4 w-4" />
              {t('publishing-connections-tab')}
            </span>
          ),
          component: (
            <div className="py-4">
              <Connections />
            </div>
          )
        });
      }
    }

    // Add common tabs for all users
    baseTabs.push({
      id: isAdmin ? 3 : 1,
      name: (
        <span className="flex items-center gap-2">
          <Keyboard className="h-4 w-4" />
          {t('shortcuts')}
        </span>
      ),
      component: (
        <div className="py-4">
          <Keymap />
        </div>
      )
    });

    baseTabs.push({
      id: isAdmin ? 4 : 2,
      name: (
        <span className="flex items-center gap-2">
          <Shield className="h-4 w-4" />
          {t('security')}
        </span>
      ),
      component: (
        <div className="py-4">
          <SecuritySettings />
          <DeleteAccountSettings />
        </div>
      )
    });

    return baseTabs;
  }, [
    t,
    isAdmin,
    publishingEnabled,
    accountControl,
    accountErrors,
    user,
    handleAccountSubmit,
    isAccountDirty,
    organizationControl,
    organizationErrors,
    organization,
    handleOrganizationSubmit,
    isOrganizationDirty,
    setValue
  ]);

  return (
    <div className="bg-app min-h-screen">
      <div className="overflow-x-hidden p-4">
        <div className="h-full">
          <Tabs tabs={tabs} searchParams minHeight="auto" maxHeight="none" />
        </div>
      </div>
    </div>
  );
}
